import React, { useEffect, useState, useCallback } from 'react';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save,
  TrendingUp,
  Target,
  Layers,
  Building2,
  Share2,
  Check,
  Globe,
  Lock,
  Users,
  BarChart3,
  Database,
  AlertTriangle,
  Clock,
  Activity,
  Shapes
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const PILLARS = [
  'Learning Reach',
  'Learning Quality', 
  'Business Alignment',
  'Operational Efficiency',
  'Custom'
];

const KPI_CATEGORIES = [
  'Productivity',
  'Quality',
  'Safety',
  'Financial',
  'Employee',
  'Customer',
  'Custom'
];

const SCOPE_OPTIONS = [
  { value: 'my_org', label: 'My Organization Only', icon: Lock, description: 'Only your organization can use this measure', color: 'border-slate-300 bg-slate-50' },
  { value: 'all', label: 'All Organizations', icon: Globe, description: 'Every organization can use this measure', color: 'border-emerald-300 bg-emerald-50' },
  { value: 'specific', label: 'Select Organizations', icon: Users, description: 'Choose which organizations can use this', color: 'border-indigo-300 bg-indigo-50' },
];

// ============== Analytics View Component ==============

const AnalyticsView = ({ leiAnalytics, kpiAnalytics, loading, orgNameMap, isSuperAdmin, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="analytics-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!leiAnalytics && !kpiAnalytics) {
    return (
      <Card className="border-dashed" data-testid="analytics-empty">
        <CardContent className="py-12 text-center">
          <BarChart3 className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No Analytics Available</h3>
          <p className="text-slate-500 mt-2">Create some custom measures first to see usage analytics.</p>
        </CardContent>
      </Card>
    );
  }

  const leiSummary = leiAnalytics?.summary || { total: 0, active: 0, unused: 0, total_data_points: 0 };
  const kpiSummary = kpiAnalytics?.summary || { total: 0, active: 0, unused: 0, total_data_points: 0 };
  const totalMeasures = leiSummary.total + kpiSummary.total;
  const totalActive = leiSummary.active + kpiSummary.active;
  const totalUnused = leiSummary.unused + kpiSummary.unused;
  const totalDP = leiSummary.total_data_points + kpiSummary.total_data_points;
  const allItems = [
    ...(leiAnalytics?.measures || []).map(m => ({ ...m, type: 'lei' })),
    ...(kpiAnalytics?.kpis || []).map(k => ({ ...k, type: 'kpi' })),
  ];
  const maxDP = Math.max(...allItems.map(i => i.data_points), 1);
  const avgHealth = allItems.length > 0
    ? Math.round(allItems.reduce((s, i) => s + (i.health_score || 0), 0) / allItems.length)
    : 0;

  const healthColor = (score) => {
    if (score >= 70) return { ring: 'text-emerald-500', bg: 'bg-emerald-50', label: 'Healthy' };
    if (score >= 40) return { ring: 'text-amber-500', bg: 'bg-amber-50', label: 'Needs Attention' };
    if (score > 0) return { ring: 'text-orange-500', bg: 'bg-orange-50', label: 'Low' };
    return { ring: 'text-rose-400', bg: 'bg-rose-50', label: 'No Data' };
  };

  return (
    <div className="space-y-6" data-testid="analytics-view">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100"><Layers className="h-5 w-5 text-slate-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Total Measures</p>
                <p className="text-2xl font-bold text-slate-900">{totalMeasures}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-active">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100"><Activity className="h-5 w-5 text-emerald-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-bold text-emerald-700">{totalActive}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-unused">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Unused</p>
                <p className="text-2xl font-bold text-amber-700">{totalUnused}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-data-points">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100"><Database className="h-5 w-5 text-indigo-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Data Points</p>
                <p className="text-2xl font-bold text-indigo-700">{totalDP.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-avg-health">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${healthColor(avgHealth).bg}`}>
                <Activity className={`h-5 w-5 ${healthColor(avgHealth).ring}`} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg Health</p>
                <p className={`text-2xl font-bold ${healthColor(avgHealth).ring}`}>{avgHealth}<span className="text-sm font-normal text-slate-400">/100</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Refresh */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-900">Measure Usage Breakdown</h3>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-analytics-btn">
          <BarChart3 className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Per-measure analytics */}
      {allItems.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">No custom measures to analyze.</p>
      ) : (
        <div className="space-y-3" data-testid="analytics-items-list">
          {allItems.map(item => {
            const usagePercent = maxDP > 0 ? Math.round((item.data_points / maxDP) * 100) : 0;
            const isLEI = item.type === 'lei';
            const hs = item.health_score || 0;
            const hc = healthColor(hs);
            const hb = item.health_breakdown || {};
            return (
              <Card key={item.id} className={`transition-shadow hover:shadow-sm ${!item.is_active ? 'opacity-70 border-dashed' : ''}`} data-testid={`analytics-item-${item.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg shrink-0 ${isLEI ? 'bg-teal-100' : 'bg-navy-100'}`}>
                      {isLEI ? <TrendingUp className="h-4 w-4 text-teal-700" /> : <Target className="h-4 w-4 text-navy-700" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-slate-900 text-sm">{item.name}</h4>
                        <Badge variant="outline" className={`text-xs ${isLEI ? 'text-teal-600 border-teal-200' : 'text-navy-600 border-navy-200'}`}>
                          {isLEI ? item.pillar : item.category}
                        </Badge>
                        {!item.is_active && (
                          <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200" data-testid="unused-badge">
                            <AlertTriangle className="h-3 w-3 mr-1" /> Unused
                          </Badge>
                        )}
                        {item.global && (
                          <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">
                            <Globe className="h-3 w-3 mr-1" /> Global
                          </Badge>
                        )}
                        {isSuperAdmin && item.organization_id && orgNameMap[item.organization_id] && (
                          <Badge variant="outline" className="text-xs text-slate-500">
                            <Building2 className="h-3 w-3 mr-1" /> {orgNameMap[item.organization_id]}
                          </Badge>
                        )}
                      </div>

                      {/* Usage bar */}
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between text-xs text-slate-600">
                          <span className="flex items-center gap-1">
                            <Database className="h-3 w-3" /> {item.data_points} data point{item.data_points !== 1 ? 's' : ''}
                          </span>
                          {item.last_data_date && (
                            <span className="flex items-center gap-1 text-slate-400">
                              <Clock className="h-3 w-3" /> Last: {new Date(item.last_data_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${isLEI ? 'bg-teal-500' : 'bg-navy-500'} ${item.data_points === 0 ? 'bg-slate-200' : ''}`}
                            style={{ width: `${Math.max(usagePercent, item.data_points > 0 ? 3 : 0)}%` }}
                            data-testid={`usage-bar-${item.id}`}
                          />
                        </div>
                      </div>

                      {/* Counts */}
                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        <span>{item.scenarios_count} scenario{item.scenarios_count !== 1 ? 's' : ''}</span>
                        <span>{item.goals_count} goal{item.goals_count !== 1 ? 's' : ''}</span>
                        {isSuperAdmin && item.data_orgs && (
                          <span>{item.data_orgs.length} org{item.data_orgs.length !== 1 ? 's' : ''} with data</span>
                        )}
                      </div>
                    </div>

                    {/* Health Score Ring */}
                    <div className="shrink-0 flex flex-col items-center gap-1" data-testid={`health-score-${item.id}`}>
                      <div className={`relative w-14 h-14 rounded-full border-4 ${hc.ring.replace('text-', 'border-')} flex items-center justify-center ${hc.bg}`}>
                        <span className={`text-lg font-bold ${hc.ring}`}>{hs}</span>
                      </div>
                      <span className={`text-[10px] font-medium ${hc.ring}`}>{hc.label}</span>
                      {/* Breakdown tooltip area */}
                      <div className="text-[9px] text-slate-400 text-center leading-tight mt-0.5" data-testid={`health-breakdown-${item.id}`}>
                        <div>Fresh: {hb.freshness || 0}/30</div>
                        <div>Data: {hb.coverage || 0}/40</div>
                        <div>Adopt: {hb.adoption || 0}/20</div>
                        <div>Goals: {hb.goals || 0}/10</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== Main Page Component ==============

const CustomMeasuresPage = () => {
  const { isSuperAdmin, user } = useAuth();
  const [customLEIs, setCustomLEIs] = useState([]);
  const [customKPIs, setCustomKPIs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editType, setEditType] = useState('lei');
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});

  // Org state
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');

  // Scope state for create/edit dialog
  const [dialogScope, setDialogScope] = useState('my_org');
  const [dialogAssignedOrgs, setDialogAssignedOrgs] = useState([]);

  // Standalone assign dialog (quick action from card)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignedOrgIds, setAssignedOrgIds] = useState([]);

  // Analytics state
  const [leiAnalytics, setLeiAnalytics] = useState(null);
  const [kpiAnalytics, setKpiAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Org name lookup
  const orgNameMap = React.useMemo(() => {
    const map = {};
    organizations.forEach(o => { map[o.id] = o.name; });
    return map;
  }, [organizations]);

  // Fetch organizations for Super Admin
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/rbac/organizations').then(res => {
      setOrganizations(res.data.organizations || []);
    }).catch(() => {});
  }, [isSuperAdmin]);

  const loadCustomMeasures = useCallback(async () => {
    try {
      const [leiRes, kpiRes] = await Promise.all([
        api.get('/custom-lei-measures'),
        api.get('/custom-kpis')
      ]);
      setCustomLEIs(leiRes.data);
      setCustomKPIs(kpiRes.data);
    } catch (error) {
      logger.error('Error loading custom measures:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required');
      }
      setCustomLEIs([]);
      setCustomKPIs([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomMeasures();
  }, [loadCustomMeasures]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [leiRes, kpiRes] = await Promise.all([
        api.get('/custom-lei-measures/usage-analytics'),
        api.get('/custom-kpis/usage-analytics')
      ]);
      setLeiAnalytics(leiRes.data);
      setKpiAnalytics(kpiRes.data);
    } catch (error) {
      logger.error('Error loading analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // Filter measures by selected org (client-side for Super Admin)
  const filteredLEIs = React.useMemo(() => {
    if (!isSuperAdmin || selectedOrg === 'all') return customLEIs;
    return customLEIs.filter(m =>
      m.organization_id === selectedOrg ||
      (m.assigned_organizations || []).includes(selectedOrg) ||
      m.global === true
    );
  }, [customLEIs, selectedOrg, isSuperAdmin]);

  const filteredKPIs = React.useMemo(() => {
    if (!isSuperAdmin || selectedOrg === 'all') return customKPIs;
    return customKPIs.filter(k =>
      k.organization_id === selectedOrg ||
      (k.assigned_organizations || []).includes(selectedOrg) ||
      k.global === true
    );
  }, [customKPIs, selectedOrg, isSuperAdmin]);

  const canEditItem = (item) => {
    if (isSuperAdmin) return true;
    return item.creator_id === user?.user_id;
  };

  const openCreateDialog = (type) => {
    setEditType(type);
    setEditItem(null);
    setDialogScope('my_org');
    setDialogAssignedOrgs([]);
    setFormData(type === 'lei' ? {
      name: '',
      description: '',
      pillar: 'Custom',
      default_weight: 0.5,
      calculation_method: 'percentage',
      unit: '%'
    } : {
      name: '',
      description: '',
      category: 'Custom',
      base_value: 100000,
      unit: '$',
      default_elasticity: 0.8
    });
    setEditDialogOpen(true);
  };

  const openEditDialog = (type, item) => {
    setEditType(type);
    setEditItem(item);
    setDialogScope(item.scope || 'my_org');
    setDialogAssignedOrgs(item.assigned_organizations || []);
    setFormData({ ...item });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const endpoint = editType === 'lei' ? '/custom-lei-measures' : '/custom-kpis';
      
      // Build payload with scope for Super Admins
      const payload = { ...formData };
      if (isSuperAdmin) {
        payload.scope = dialogScope;
        payload.assigned_organizations = dialogScope === 'specific' ? dialogAssignedOrgs : [];
      }

      if (editItem) {
        await api.put(`${endpoint}/${editItem.id}`, payload);
        toast.success(`${editType === 'lei' ? 'LEI Measure' : 'KPI'} updated!`);
      } else {
        await api.post(endpoint, payload);
        toast.success(`${editType === 'lei' ? 'LEI Measure' : 'KPI'} created!`);
      }
      
      setEditDialogOpen(false);
      loadCustomMeasures();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save. Please try again.';
      toast.error(msg);
    }
  };

  const handleDelete = async (type, id) => {
    try {
      const endpoint = type === 'lei' ? '/custom-lei-measures' : '/custom-kpis';
      await api.delete(`${endpoint}/${id}`);
      toast.success('Deleted successfully');
      loadCustomMeasures();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete';
      toast.error(msg);
    }
  };

  // Quick assign dialog from card
  const openAssignDialog = (type, item) => {
    setAssignTarget({ type, item });
    setAssignedOrgIds(item.assigned_organizations || []);
    setAssignDialogOpen(true);
  };

  const toggleOrgAssignment = (orgId) => {
    setAssignedOrgIds(prev =>
      prev.includes(orgId) ? prev.filter(id => id !== orgId) : [...prev, orgId]
    );
  };

  const handleAssignOrgs = async () => {
    if (!assignTarget) return;
    try {
      const { type, item } = assignTarget;
      const endpoint = type === 'lei'
        ? `/custom-lei-measures/${item.id}/assign-orgs`
        : `/custom-kpis/${item.id}/assign-orgs`;
      await api.post(endpoint, { organization_ids: assignedOrgIds });
      toast.success(`Assigned to ${assignedOrgIds.length} organization(s)`);
      setAssignDialogOpen(false);
      loadCustomMeasures();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to assign organizations';
      toast.error(msg);
    }
  };

  // Toggle org in dialog scope multi-select
  const toggleDialogOrg = (orgId) => {
    setDialogAssignedOrgs(prev =>
      prev.includes(orgId) ? prev.filter(id => id !== orgId) : [...prev, orgId]
    );
  };

  // Scope badge for cards
  const ScopeBadge = ({ item }) => {
    if (item.global) {
      return (
        <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300" data-testid="scope-badge-global">
          <Globe className="h-3 w-3 mr-1" />
          All Orgs
        </Badge>
      );
    }
    if ((item.assigned_organizations || []).length > 0) {
      return (
        <Badge variant="outline" className="text-xs text-violet-600 border-violet-200 bg-violet-50" data-testid="scope-badge-shared">
          <Share2 className="h-3 w-3 mr-1" />
          Shared with {item.assigned_organizations.length} org{item.assigned_organizations.length > 1 ? 's' : ''}
        </Badge>
      );
    }
    if (isSuperAdmin) {
      return (
        <Badge variant="outline" className="text-xs text-slate-500 border-slate-200" data-testid="scope-badge-private">
          <Lock className="h-3 w-3 mr-1" />
          Org Only
        </Badge>
      );
    }
    return null;
  };

  // Org badge
  const OrgBadge = ({ orgId }) => {
    const name = orgNameMap[orgId] || orgId;
    if (!orgId) return null;
    return (
      <Badge variant="outline" className="text-xs text-teal-600 border-teal-200 bg-teal-50" data-testid="owner-org-badge">
        <Building2 className="h-3 w-3 mr-1" />
        {name}
      </Badge>
    );
  };

  // Measure card
  const MeasureCard = ({ item, type }) => {
    const isLEI = type === 'lei';
    const editable = canEditItem(item);

    return (
      <Card className="hover:shadow-md transition-shadow" data-testid={`${type}-card-${item.id}`}>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-slate-900">{item.name}</h4>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  isLEI ? 'bg-teal-100 text-teal-700' : 'bg-navy-100 text-navy-700'
                }`}>
                  {isLEI ? item.pillar : item.category}
                </span>
                {isSuperAdmin && item.organization_id && <OrgBadge orgId={item.organization_id} />}
                <ScopeBadge item={item} />
              </div>
              {item.creator_email && isSuperAdmin && (
                <p className="text-xs text-slate-400 mt-1">Created by {item.creator_email}</p>
              )}
              <p className="text-sm text-slate-500 mt-1">{item.description}</p>
              <div className="flex gap-4 mt-3 text-xs text-slate-600">
                {isLEI ? (
                  <>
                    <span>Weight: <strong>{item.default_weight}</strong></span>
                    <span>Unit: <strong>{item.unit}</strong></span>
                  </>
                ) : (
                  <>
                    <span>Base Value: <strong>{item.unit}{item.base_value?.toLocaleString()}</strong></span>
                    <span>Elasticity: <strong>{item.default_elasticity}</strong></span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {isSuperAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openAssignDialog(type, item)}
                  title="Assign to organizations"
                  data-testid={`assign-orgs-${type}-${item.id}`}
                >
                  <Share2 className="h-4 w-4 text-indigo-500" />
                </Button>
              )}
              {editable && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(type, item)}
                    data-testid={`edit-${type}-${item.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-rose-500 hover:text-rose-700" data-testid={`delete-${type}-${item.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {isLEI ? 'LEI Measure' : 'KPI'}</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{item.name}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(type, item.id)}
                          className="bg-rose-600 hover:bg-rose-700"
                          data-testid={`confirm-delete-${type}-${item.id}`}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Org checkbox list (reused in dialog scope & assign dialog)
  const OrgCheckboxList = ({ selected, onToggle, excludeOrgId, maxHeight = '200px' }) => {
    const filteredOrgs = organizations.filter(o => o.id !== excludeOrgId);
    if (filteredOrgs.length === 0) {
      return <p className="text-sm text-slate-500 text-center py-4">No other organizations available</p>;
    }
    return (
      <div className="space-y-1.5 overflow-y-auto" style={{ maxHeight }}>
        {filteredOrgs.map(org => {
          const isSelected = selected.includes(org.id);
          return (
            <button
              key={org.id}
              type="button"
              onClick={() => onToggle(org.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors text-left text-sm ${
                isSelected
                  ? 'border-indigo-300 bg-indigo-50'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
              data-testid={`org-checkbox-${org.id}`}
            >
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                isSelected ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-slate-300'
              }`}>
                {isSelected && <Check className="h-2.5 w-2.5" />}
              </div>
              <span className="truncate">{org.name}</span>
            </button>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="custom-measures-loading">
        <div className="spinner" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  const __heroActions = isSuperAdmin && organizations.length > 0 ? (
    <div className="flex items-center gap-2" data-testid="org-filter">
      <Building2 className="h-4 w-4 text-slate-700" />
      <select
        value={selectedOrg}
        onChange={(e) => setSelectedOrg(e.target.value)}
        className="bg-white border border-slate-200 text-slate-700 rounded-lg px-3 py-1.5 text-sm backdrop-blur-sm [&>option]:text-slate-900"
        data-testid="org-filter-select"
      >
        <option value="all">All Organizations</option>
        {organizations.map(o => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  ) : null;
  const __heroStats = [
    {
      label: 'Custom LEI Measures',
      value: filteredLEIs.length,
      testId: 'lei-count',
      whyPanel: {
        label: 'Custom LEI Measures',
        value: filteredLEIs.length,
        valueSuffix: 'measures',
        description: 'Number of organization-specific LEI measures you have defined beyond the system defaults. Custom measures let you weight learning signals to match your business reality.',
        formula: 'count(LEI measures WHERE custom = true)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A small number of well-defined custom measures (1–3) usually beats a long list. Each custom measure should answer a specific business question your defaults don\'t.' },
          { label: 'What to do', value: '', hint: filteredLEIs.length === 0
            ? 'No custom LEI measures yet. If your defaults are sufficient, that\'s fine. Otherwise, click the LEI tab below and add a measure tuned to your industry.'
            : 'Review elasticities periodically — incorrect elasticities propagate into every ROI calculation that uses these measures.' },
        ],
        links: [
          { label: 'Open LEI Dashboard', path: '/lei-dashboard' },
        ],
      },
    },
    {
      label: 'Custom KPIs',
      value: filteredKPIs.length,
      testId: 'kpi-count',
      whyPanel: {
        label: 'Custom KPIs',
        value: filteredKPIs.length,
        valueSuffix: 'KPIs',
        description: 'Number of organization-specific KPIs you have created (e.g., revenue per agent, claim cycle time, NPS). These appear in the Business Case Builder for scenario projections.',
        formula: 'count(KPIs WHERE custom = true)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Custom KPIs should map directly to a P&L line or operational metric your CFO already tracks. Vanity metrics dilute your business case.' },
          { label: 'What to do', value: '', hint: filteredKPIs.length === 0
            ? 'No custom KPIs yet. Open the KPI tab below to create one — start with the metric your CFO asks about most often.'
            : 'Audit each KPI quarterly: confirm baseline, units, and currency are still accurate before saving new scenarios.' },
        ],
        links: [
          { label: 'Build a scenario', path: '/business-case-builder' },
        ],
      },
    },
    {
      label: 'Total Custom',
      value: filteredLEIs.length + filteredKPIs.length,
      testId: 'total-count',
      whyPanel: {
        label: 'Total Custom Measures',
        value: filteredLEIs.length + filteredKPIs.length,
        valueSuffix: 'items',
        description: 'Combined count of custom LEI measures and KPIs in your tenant. This is the breadth of the bespoke metrics layer your scenarios can draw from.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Breadth matters less than fit — 5 custom items that map cleanly to your P&L outperform 25 generic ones.' },
          { label: 'What to do', value: '', hint: 'Use the search/filter below to spot duplicates or near-duplicates and merge them. Cleaner taxonomy = clearer scenarios.' },
        ],
      },
    },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Custom Measures"
        description="Create and manage custom LEI measures and KPIs specific to your organization. Define your own metrics with appropriate weights and elasticities for accurate ROI calculations."
        theme={__pageTheme}
        icon={Shapes}
        actions={__heroActions}
        stats={__heroStats}
        testId="custom-measures-hero"
      />
    )}>
    <div className="space-y-8 animate-fade-in" data-testid="custom-measures-page">

      {/* Tabs */}
      <Tabs defaultValue="lei" className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="lei" className="flex items-center gap-2" data-testid="tab-lei">
            <TrendingUp className="h-4 w-4" />
            LEI Measures
          </TabsTrigger>
          <TabsTrigger value="kpi" className="flex items-center gap-2" data-testid="tab-kpi">
            <Target className="h-4 w-4" />
            KPIs
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2" data-testid="tab-analytics" onClick={() => { if (!leiAnalytics) loadAnalytics(); }}>
            <BarChart3 className="h-4 w-4" />
            Usage Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lei" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Custom LEI Measures</h3>
            <Button 
              onClick={() => openCreateDialog('lei')}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="create-lei-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create LEI Measure
            </Button>
          </div>

          {filteredLEIs.length === 0 ? (
            <Card className="border-dashed" data-testid="no-lei-placeholder">
              <CardContent className="py-12">
                <div className="text-center">
                  <TrendingUp className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900">No Custom LEI Measures</h3>
                  <p className="text-slate-500 mt-2 max-w-md mx-auto">
                    Create custom LEI measures to track training metrics specific to your organization.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="lei-measures-list">
              {filteredLEIs.map(lei => <MeasureCard key={lei.id} item={lei} type="lei" />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kpi" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Custom KPIs</h3>
            <Button 
              onClick={() => openCreateDialog('kpi')}
              className="bg-navy-700 hover:bg-navy-800"
              data-testid="create-kpi-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create KPI
            </Button>
          </div>

          {filteredKPIs.length === 0 ? (
            <Card className="border-dashed" data-testid="no-kpi-placeholder">
              <CardContent className="py-12">
                <div className="text-center">
                  <Target className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900">No Custom KPIs</h3>
                  <p className="text-slate-500 mt-2 max-w-md mx-auto">
                    Create custom KPIs to measure business outcomes specific to your organization.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="kpis-list">
              {filteredKPIs.map(kpi => <MeasureCard key={kpi.id} item={kpi} type="kpi" />)}
            </div>
          )}
        </TabsContent>

        {/* Usage Analytics Tab */}
        <TabsContent value="analytics" className="mt-6" data-testid="analytics-tab-content">
          <AnalyticsView
            leiAnalytics={leiAnalytics}
            kpiAnalytics={kpiAnalytics}
            loading={analyticsLoading}
            orgNameMap={orgNameMap}
            isSuperAdmin={isSuperAdmin}
            onRefresh={loadAnalytics}
          />
        </TabsContent>
      </Tabs>
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editItem ? 'Edit' : 'Create'} {editType === 'lei' ? 'LEI Measure' : 'KPI'}
            </DialogTitle>
            <DialogDescription>
              {editType === 'lei' 
                ? 'Define a custom LEI measure for your training metrics.'
                : 'Define a custom KPI to measure business outcomes.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={editType === 'lei' ? 'e.g., Leadership Training Hours' : 'e.g., Customer Retention Rate'}
                data-testid="measure-name-input"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this measure tracks..."
                rows={2}
                data-testid="measure-description-input"
              />
            </div>

            {editType === 'lei' ? (
              <>
                <div className="grid gap-2">
                  <Label>Pillar</Label>
                  <Select
                    value={formData.pillar || 'Custom'}
                    onValueChange={(value) => setFormData({ ...formData, pillar: value })}
                  >
                    <SelectTrigger data-testid="pillar-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PILLARS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>Default Weight: {formData.default_weight?.toFixed(2)}</Label>
                  <Slider
                    value={[formData.default_weight || 0.5]}
                    onValueChange={([value]) => setFormData({ ...formData, default_weight: value })}
                    max={1}
                    step={0.05}
                    className="py-2"
                    data-testid="weight-slider"
                  />
                  <p className="text-xs text-slate-500">
                    How strongly this measure typically affects KPIs (0 = no impact, 1 = maximum impact)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Unit</Label>
                    <Select
                      value={formData.unit || '%'}
                      onValueChange={(value) => setFormData({ ...formData, unit: value })}
                    >
                      <SelectTrigger data-testid="unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="%">Percentage (%)</SelectItem>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="score">Score</SelectItem>
                        <SelectItem value="count">Count</SelectItem>
                        <SelectItem value="$">Currency ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label>Category</Label>
                  <Select
                    value={formData.category || 'Custom'}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {KPI_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Base Value</Label>
                    <Input
                      type="number"
                      value={formData.base_value || 0}
                      onChange={(e) => setFormData({ ...formData, base_value: parseFloat(e.target.value) })}
                      placeholder="100000"
                      data-testid="base-value-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Unit</Label>
                    <Select
                      value={formData.unit || '$'}
                      onValueChange={(value) => setFormData({ ...formData, unit: value })}
                    >
                      <SelectTrigger data-testid="kpi-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="$">Currency ($)</SelectItem>
                        <SelectItem value="%">Percentage (%)</SelectItem>
                        <SelectItem value="score">Score</SelectItem>
                        <SelectItem value="count">Count</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Default Elasticity: {formData.default_elasticity?.toFixed(2)}</Label>
                  <Slider
                    value={[formData.default_elasticity || 0.8]}
                    onValueChange={([value]) => setFormData({ ...formData, default_elasticity: value })}
                    max={2}
                    step={0.1}
                    className="py-2"
                    data-testid="elasticity-slider"
                  />
                  <p className="text-xs text-slate-500">
                    How responsive this KPI is to learning improvements (0.5 = low, 1.0 = moderate, 1.5+ = high)
                  </p>
                </div>
              </>
            )}

            {/* Organization Scope (Super Admin only) */}
            {isSuperAdmin && (
              <div className="grid gap-3 pt-2 border-t" data-testid="scope-section">
                <Label className="text-sm font-semibold">Organization Scope</Label>
                <div className="space-y-2">
                  {SCOPE_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const isActive = dialogScope === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setDialogScope(opt.value)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition-all text-left ${
                          isActive ? opt.color + ' ring-1 ring-offset-1' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`scope-option-${opt.value}`}
                      >
                        <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-slate-700' : 'text-slate-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                            {opt.label}
                          </p>
                          <p className="text-xs text-slate-500">{opt.description}</p>
                        </div>
                        {isActive && (
                          <div className="w-4 h-4 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
                            <Check className="h-2.5 w-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Org multi-select when "specific" is chosen */}
                {dialogScope === 'specific' && (
                  <div className="mt-1" data-testid="specific-orgs-list">
                    <p className="text-xs text-slate-500 mb-2">
                      Select organizations ({dialogAssignedOrgs.length} selected)
                    </p>
                    <OrgCheckboxList
                      selected={dialogAssignedOrgs}
                      onToggle={toggleDialogOrg}
                      maxHeight="160px"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="cancel-measure-btn">
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              className={editType === 'lei' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-navy-700 hover:bg-navy-800'}
              data-testid="save-measure-btn"
            >
              <Save className="mr-2 h-4 w-4" />
              {editItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Assign Dialog (from card action) */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Assign to Organizations</DialogTitle>
            <DialogDescription>
              Share "{assignTarget?.item?.name}" with other organizations. 
              They will be able to view and use this {assignTarget?.type === 'lei' ? 'measure' : 'KPI'}.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <OrgCheckboxList
              selected={assignedOrgIds}
              onToggle={toggleOrgAssignment}
              excludeOrgId={assignTarget?.item?.organization_id}
              maxHeight="320px"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)} data-testid="cancel-assign-btn">
              Cancel
            </Button>
            <Button onClick={handleAssignOrgs} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-assign-btn">
              <Share2 className="mr-2 h-4 w-4" />
              Save Assignments ({assignedOrgIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
};

export default CustomMeasuresPage;
